import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { isTrackUsable } from '@/lib/voip/audioDevices';
import {
  currentMicTrack,
  liveCallsOf,
  useVoipStore,
  type AudioRecoveryResult,
} from '@/stores/voip.store';

/** Chrome reports one unplug as a burst of `devicechange` events; wait for them
 *  to stop before reading the list, or we read it mid-change. */
const DEVICE_SETTLE_MS = 400;

const SILENCE_SAMPLE_MS = 500;
/** How long a live, unmuted microphone may send nothing before we go and check
 *  the device underneath it. */
const SILENCE_TRIGGER_MS = 5_000;

/**
 * Keeps a live call on a working microphone.
 *
 * Our softphone is WebRTC in a tab, so when the headset leaves the OS device
 * list the `MediaStreamTrack` bound to it ends and never recovers — the call
 * stays up on a dead device and the CSR has to reload. A desktop softphone
 * watches the OS for this and re-routes the call itself; this is that handler.
 *
 * The browser gives three hints that the device under a call has moved, and all
 * three are needed:
 *
 * - `devicechange` — the headset came back, or a dock appeared.
 * - `ended` / `mute` on the mic track — it went away.
 * - the tab becoming visible again — waking from sleep, where neither of the
 *   other two is guaranteed to have fired while we were suspended.
 *
 * Each one asks the store to re-resolve devices and swap the track underneath
 * the call, so the call never drops and the other party hears nothing.
 *
 * Mounted once, in the dashboard layout, alongside the other global call parts.
 */
export function useAudioDeviceRecovery(): void {
  const live = useVoipStore(useShallow((s) => liveCallsOf(s.calls)));
  const recover = useVoipStore((s) => s.recoverAudioDevices);
  const hasLiveCall = live.length > 0;

  const run = useCallback(() => {
    void recover().then(announce).catch(noop);
  }, [recover]);

  // The OS device list changed, or we came back from sleep.
  useEffect(() => {
    if (!hasLiveCall) return;
    let settle: ReturnType<typeof setTimeout> | undefined;
    const onDeviceChange = () => {
      clearTimeout(settle);
      settle = setTimeout(run, DEVICE_SETTLE_MS);
    };
    const onVisible = () => {
      // Only when something is actually broken: a tab switch is not a device
      // change, and re-routing audio on every one of them would be noise.
      if (document.visibilityState !== 'visible') return;
      const broken = liveCallsOf(useVoipStore.getState().calls).some(
        (entry) => !isTrackUsable(currentMicTrack(entry)),
      );
      if (broken) run();
    };

    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(settle);
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [hasLiveCall, run]);

  // The track itself died. `ended` is the device disappearing; `mute` is the OS
  // no longer delivering samples, which is what an exclusive grab by another app
  // looks like. Re-bound whenever a call's stream changes — including after a
  // swap, so the replacement track is watched too.
  useEffect(() => {
    const detach = live.map((entry) => {
      const track = currentMicTrack(entry);
      if (!track) return noop;
      track.addEventListener('ended', run);
      track.addEventListener('mute', run);
      return () => {
        track.removeEventListener('ended', run);
        track.removeEventListener('mute', run);
      };
    });
    return () => detach.forEach((off) => off());
  }, [live, run]);

  useSilenceWatchdog(
    live.find((c) => c.status === 'active' && !c.isMuted)?._localStream ?? null,
    run,
  );
}

/**
 * Nudges a re-check when a live microphone sends nothing at all for a few
 * seconds — the case where the device is gone but the browser never told us.
 *
 * It only triggers the same recovery pass the other signals do; it never warns
 * on its own. With echo cancellation and noise suppression on, a quiet room
 * genuinely produces digital silence, so treating that as a dead microphone
 * would put a warning in front of a CSR whose headset is fine. If the device
 * really has moved, recovery swaps it and says so; if it hasn't, nothing
 * happens.
 */
function useSilenceWatchdog(stream: MediaStream | null, run: () => void): void {
  useEffect(() => {
    if (!stream) return;
    const AudioCtx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;

    let ctx: AudioContext;
    let analyser: AnalyserNode;
    try {
      ctx = new AudioCtx();
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
    } catch {
      // No usable audio graph for this stream — skip the watchdog entirely.
      return;
    }
    // The call began with a click (dial or answer), so the context is allowed to
    // run; without the resume it can sit suspended and read silence forever.
    void ctx.resume?.().catch(noop);

    const data = new Uint8Array(analyser.frequencyBinCount);
    let silentFor = 0;
    const timer = setInterval(() => {
      analyser.getByteTimeDomainData(data);
      const silent = data.every((sample) => sample === 128);
      silentFor = silent ? silentFor + SILENCE_SAMPLE_MS : 0;
      if (silentFor < SILENCE_TRIGGER_MS) return;
      silentFor = 0;
      run();
    }, SILENCE_SAMPLE_MS);

    return () => {
      clearInterval(timer);
      void ctx.close().catch(noop);
    };
  }, [stream, run]);
}

function announce(result: AudioRecoveryResult): void {
  if (result.failed) {
    toast.error('No working microphone — check your headset connection');
    return;
  }
  if (!result.swapped) return;
  toast(result.micLabel ? `Microphone switched to ${result.micLabel}` : 'Microphone reconnected');
}

function noop(): void {}
